import WorkOrderStatusPill from '@atom/components/common/workOrderDetail/workOrderStatusPill/WorkOrderStatusPill';
import WorkPriorityIcon from '@atom/components/common/WorkPriorityIcon';
import WorkTasks from '@atom/components/common/workTasks/WorkTasks';
import WorkUsers from '@atom/components/common/workUsers/WorkUsers';
import { useNavigation } from '@atom/hooks/useNavigation';
import { ListTable, Progress } from '@atom/mui';
import { WorkOrder } from '@atom/types/work';
import { setDisplayDate } from '@atom/utilities/timeUtilities';
import { rollUpTaskUsers } from '@atom/utilities/userUtilities';
import { getWorKOrderListPriorityLabel } from '@atom/utilities/workOrderPriorityUtilities';

import {
  RESULTS_PER_PAGE_OPTIONS,
  SortDirection,
} from './useWorkOrdersPageState';

import classes from './workOrdersTab.module.css';

const {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
} = ListTable;

const styles = {
  tableContainer: {
    height: `calc(100% - 6.5rem)`,
  },
  progress: {
    height: '100%',
  },
  link: {
    fontWeight: '500',
  },
};

type WorkOrdersColumn = {
  label: string;
  sortField?: string;
};

const COLUMNS: WorkOrdersColumn[] = [
  { label: 'Work ID', sortField: 'name' },
  { label: 'Tasks' },
  { label: 'Work Template' },
  { label: 'Status', sortField: 'statusId' },
  { label: 'Priority' },
  { label: 'Assigned To' },
  { label: 'Due Date', sortField: 'dueDate' },
  { label: 'Completed Date', sortField: 'completionDate' },
  { label: 'Inventory Update Date', sortField: 'appliedDate' },
];

type WorkOrdersTableProps = {
  workOrders: WorkOrder[];
  loadingWorkOrders: boolean;
  totalCount: number;
  page: number;
  limit: number;
  sortBy: string;
  sortDirection: SortDirection;
  onSortChange: (sortBy: string, sortDirection: SortDirection) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
};

const WorkOrdersTable = ({
  workOrders,
  loadingWorkOrders,
  totalCount,
  page,
  limit,
  sortBy,
  sortDirection,
  onSortChange,
  onPageChange,
  onLimitChange,
}: WorkOrdersTableProps) => {
  const [, { Link }] = useNavigation();

  if (loadingWorkOrders) {
    return <Progress style={styles.progress} />;
  }

  return (
    <>
      <div className={classes.tableHeaderSpacer}>{`${totalCount} Work`}</div>
      <ListTable TableContainerProps={{ style: styles.tableContainer }}>
        <TableHead>
          <TableRow header>
            {COLUMNS.map(({ label, sortField }) => (
              <TableCell
                key={label}
                variant="head"
                {...(sortField && {
                  sortDirection: sortBy === sortField ? sortDirection : false,
                  onSortChange: (direction: SortDirection) =>
                    onSortChange(sortField, direction),
                })}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {workOrders?.map(workOrder => (
            <TableRow
              key={workOrder.id}
              data-cy={`workOrdersTableRow-${workOrder.id}`}
            >
              <TableCell>
                <Link
                  style={styles.link}
                  to={`/workOrders/${workOrder.id}`}
                  data-cy={`workOrdersTableLink-${workOrder.name}`}
                >
                  {workOrder.name}
                </Link>
              </TableCell>
              <TableCell>
                <WorkTasks tasks={workOrder?.tasks} width="10rem" />
              </TableCell>
              <TableCell>{workOrder.workTemplate?.name || '-'}</TableCell>
              <TableCell>
                <WorkOrderStatusPill
                  className="table"
                  statusId={workOrder.statusId}
                />
              </TableCell>
              <TableCell>
                <WorkPriorityIcon priorityId={workOrder.priorityId} />
                <span>
                  {getWorKOrderListPriorityLabel(workOrder.priorityId)}
                </span>
              </TableCell>
              <TableCell>
                <WorkUsers
                  users={rollUpTaskUsers(workOrder.tasks as any)}
                  userGroups={workOrder.tasks.flatMap(task => task.userGroups)}
                />
              </TableCell>
              <TableCell>{setDisplayDate(workOrder.dueDate)}</TableCell>
              <TableCell>{setDisplayDate(workOrder.completionDate)}</TableCell>
              <TableCell>{setDisplayDate(workOrder.appliedDate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={RESULTS_PER_PAGE_OPTIONS}
              count={totalCount}
              rowsPerPage={limit}
              page={page}
              onPageChange={onPageChange}
              onRowsPerPageChange={event => onLimitChange(+event.target.value)}
            />
          </TableRow>
        </TableFooter>
      </ListTable>
    </>
  );
};

export default WorkOrdersTable;
